/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import React, { useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { colors, fonts, mixins } from '@blend/consumer-components';

import { notaryAPI } from '../../../api/notary';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { ActionButton } from '@components/ActionButton/ActionButton';
import { DocumentSigningType, DocumentStatus } from '@shared/constants/document';

export namespace DocumentToolbar {
  export interface Props {
    ownerId: string;
    fetchEditUrl: () => void;
    onFinalizeDocument: (documentId: string) => Promise<void>;
    isTaggingActive: boolean;
  }
}

export const docToolbarHeight = 64;

const styles = StyleSheet.create({
  toolbar: {
    // toolbar
    display: 'flex',
    backgroundColor: colors.catalystLight01,
    height: docToolbarHeight,
    width: '100%',
    position: 'relative',
    boxShadow: mixins.boxShadow(-1, 1, 1, 1, mixins.fade(colors.black, 0.15)),
  },
  wrapper: {
    // document name & buttons
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    ...mixins.margin(0, 10, 0, 10),
  },
  textWrapper: {
    fontWeight: 'bold',
    ...mixins.padding(22, 0, 22),
  },
  buttonsWrapper: {
    display: 'flex',
    height: '100%',
    ...mixins.padding(10, 0, 14, 0),
  },
  buttonWrapper: {
    ...mixins.margin(5),
  },
  button: {
    fontSize: 16,
    fontWeight: fonts.weight.medium,
    width: 'auto',
  },
});

export const DocumentToolbar: React.FC<DocumentToolbar.Props> = ({
  ownerId,
  fetchEditUrl,
  onFinalizeDocument,
  isTaggingActive,
}) => {
  const [loading, setLoading] = useState(false);
  const { currentDoc } = useRootSelector(state => ({ currentDoc: state.document.currentDoc }));
  if (!currentDoc) {
    return null;
  }

  const DocumentManagementToolbarOptions = [];
  const DiscardedDocumentManagementToolbarOptions = [];
  if (currentDoc.type !== DocumentSigningType.ENOTE) {
    if (!isTaggingActive) {
      if (currentDoc.status === DocumentStatus.COMPLETED) {
        DocumentManagementToolbarOptions.push({
          name: 'DownloadUnsignedCopy',
          display: 'Download Unsigned Copy',
          onItemClick: async () => {
            await notaryAPI.documents.saveRoomDocumentPdf(ownerId, currentDoc.roomDocumentId!);
          },
        });
      }
      DocumentManagementToolbarOptions.push({
        name: 'DownloadDocument',
        display: 'Download',
        onItemClick: async () => {
          await notaryAPI.documents.saveRoomDocumentPdf(ownerId, currentDoc.id);
        },
      });
    }

    if (currentDoc.status !== DocumentStatus.COMPLETED) {
      if (currentDoc.status !== DocumentStatus.VOIDED) {
        if (!isTaggingActive) {
          DocumentManagementToolbarOptions.push({
            name: 'DiscardDocument',
            display: 'Delete',
            onItemClick: async () => {
              if (loading) {
                return;
              } // Prevent multiple clicks
              setLoading(true);
              try {
                await notaryAPI.documents.discardDocument(ownerId, currentDoc.id);
              } finally {
                setLoading(false);
              }
            },
          });
          DocumentManagementToolbarOptions.push({
            name: 'EditDocumentTags',
            display: 'Tag',
            onItemClick: () => {
              fetchEditUrl();
            },
          });
        }
      }

      if (currentDoc.status === DocumentStatus.EDITING && currentDoc.showControls && isTaggingActive) {
        DocumentManagementToolbarOptions.push({
          name: 'FinishEditing',
          display: 'Finish editing',
          onItemClick: async () => {
            await onFinalizeDocument(currentDoc.id);
          },
        });
      }
    }

    DiscardedDocumentManagementToolbarOptions.push(
      {
        name: 'DownloadDocument',
        display: 'Download',
        onItemClick: async () => {
          await notaryAPI.documents.saveRoomDocumentPdf(ownerId, currentDoc.id);
        },
      },
      {
        name: 'RestoreDocument',
        display: 'Restore',
        onItemClick: async () => {
          if (loading) {
            return;
          } // Prevent multiple clicks
          setLoading(true);
          try {
            await notaryAPI.documents.restoreDocument(ownerId, currentDoc.id);
          } finally {
            setLoading(false);
          }
        },
      },
    );
  }

  const toolbarOptions =
    currentDoc.status === DocumentStatus.DISCARDED
      ? DiscardedDocumentManagementToolbarOptions
      : DocumentManagementToolbarOptions;

  return (
    <div data-it="document-toolbar" className={css(styles.toolbar)}>
      <div className={css(styles.wrapper)}>
        <div className={css(styles.textWrapper)} />
        <div className={css(styles.buttonsWrapper)}>
          {toolbarOptions.map(t => (
            <div className={css(styles.buttonWrapper)} key={`toolbar-${t.display}`}>
              <ActionButton
                data-dd-privacy="allow"
                data-it={`button-${t.display}`}
                type="button"
                option={t.display === 'Tag' || t.display === 'Finish editing' ? 'primary' : 'secondary'}
                sizing="sm"
                onClick={() => t.onItemClick()}
                customStyle={styles.button}
                disabled={loading} // Disable the button while loading
              >
                {t.display}
              </ActionButton>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
